/*
 * assets/js/src/pasy.js — vodorovné pásy na homepage (`.rcar`).
 *
 * Dva pásy: „Nejoblíbenější programy" (5 karet) a „Co o nás říkají klienti"
 * (8 karet). Přeneseno z inline skriptu návrhové sady (`navrh-outdoor.html`),
 * včetně rychlosti i kroku šipek.
 *
 * ---------------------------------------------------------------------
 * DVA REŽIMY, JEDEN MODUL
 * ---------------------------------------------------------------------
 * DESKTOP (nad 767 px): pás jede plynule doleva přes `transform` v rAF
 * smyčce, pod myší se zastaví a šipky přidají jednorázový posun o kartu.
 * To je chování z návrhu a nemění se.
 *
 * MOBIL (do 767 px): `.rviewport` je nativní scroll se `scroll-snap`
 * (CSS, blok `max-width: 767px`), takže se pásem swipuje prstem. Marquee
 * by se s tím pral — CSS mu proto ruší `transform`. Od 19. 9. 2026
 * (rozhodnutí majitele) k tomu přibyl JEMNÝ SAMOVOLNÝ POSUN: každé
 * čtyři vteřiny o jednu kartu, `scrollBy` se `smooth`.
 *
 * ---------------------------------------------------------------------
 * PROČ SE STOPA ZDVOJUJE
 * ---------------------------------------------------------------------
 * Nekonečná smyčka bez švu potřebuje dvě kopie obsahu za sebou: posun se
 * vrací o polovinu šířky, což je přesně jedna kopie, a oko to nepozná.
 * Platí to pro oba režimy — na desktopu se vrací `x`, na mobilu
 * `scrollLeft`. Ze dvou možností, které zadání nabízelo (klon, nebo skok
 * z poslední karty na první), je tohle ta BEZ VIZUÁLNÍHO SKOKU: skok
 * o celou stopu by byl vidět, skok o přesnou kopii ne.
 *
 * Kopie jsou pro čtečku duplicitní, proto dostávají `aria-hidden`
 * a `tabindex="-1"` na odkazech uvnitř — NAD RÁMEC NÁVRHU, který kopii
 * dělá holým `innerHTML += innerHTML` a duplicitu čtečce nechává.
 *
 * ---------------------------------------------------------------------
 * KDY SE POHYB NEDĚJE
 * ---------------------------------------------------------------------
 * 1. `prefers-reduced-motion: reduce` — ani marquee, ani samovolný posun.
 * 2. PRVNÍ DOTYK UŽIVATELE na pásu posun zastaví TRVALE, ne dočasně.
 *    Je to mechanismus podle WCAG 2.2.2: kdo převzal kontrolu, nemá
 *    o ni po pár vteřinách zase přijít. Znovu se rozjede až po načtení
 *    stránky.
 * 3. Pás mimo viewport se nehýbe (IntersectionObserver) — jinak by se
 *    recenze posouvaly, zatímco čtenář je o tři obrazovky jinde, a po
 *    návratu by našel jiné karty, než kde skončil.
 * 4. Skrytá karta prohlížeče pohyb zastaví (`visibilitychange`).
 */

/** Práh, pod kterým je pás nativní scroll místo marquee. Shodný s CSS. */
const MOBIL = '(max-width: 767px)';

/** Jak často se posune o jednu kartu. Zadání majitele: „~4 s". */
const PERIODA = 4000;

/**
 * Krok = šířka karty + mezera. `getBoundingClientRect()` mezeru neobsahuje,
 * takže bez `column-gap` by se chyba sčítala: u osmi karet a mezery 14 px
 * je to 112 px, tedy třetina karty. Týž výpočet má `carousel.js`.
 */
function krokPasu(stopa) {
	const prvni = stopa.children[0];
	if (!prvni) return 0;
	const cs = window.getComputedStyle(stopa);
	const gap = parseFloat(cs.columnGap || cs.gap || '0') || 0;
	return prvni.getBoundingClientRect().width + gap;
}

export function init() {
	const pasy = [...document.querySelectorAll('.rcar')];
	if (!pasy.length) return 0;

	const klid = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
	const mqMobil = window.matchMedia ? window.matchMedia(MOBIL) : null;
	const jeMobil = () => !!(mqMobil && mqMobil.matches);

	let pocet = 0;
	for (const root of pasy) {
		if (root.dataset.za2026Pas === '1') continue;
		const stopa = root.querySelector('.rtrack');
		const vyrez = root.querySelector('.rviewport');
		if (!stopa || !vyrez || !stopa.children.length) continue;
		root.dataset.za2026Pas = '1';
		pocet += 1;

		// Druhá kopie stopy — pro čtečku neviditelná.
		const kopie = [...stopa.children].map((el) => {
			const k = el.cloneNode(true);
			k.setAttribute('aria-hidden', 'true');
			for (const a of k.querySelectorAll('a, button')) a.setAttribute('tabindex', '-1');
			return k;
		});
		for (const k of kopie) stopa.appendChild(k);

		/* ---------------------------------------------------------------
		 * DESKTOPOVÝ MARQUEE (beze změny proti návrhu)
		 * ------------------------------------------------------------- */
		let x = null;
		let skok = 0;
		let stoji = klid;
		let krok = 285;
		let rafBezi = false;
		const RYCHLOST = -0.35;

		const snimek = () => {
			/*
			 * POD 768 px SE SMYČKA NEPLÁNUJE ZNOVU. Do 19. 9. běžela pořád
			 * a jen jí CSS rušilo `transform: none !important` — tedy
			 * šedesátkrát za vteřinu výpočet, který nikdo neviděl. Teď se
			 * na mobilu zastaví a při návratu nad 768 px zase rozjede.
			 */
			if (jeMobil()) { rafBezi = false; return; }
			const pul = stopa.scrollWidth / 2;
			if (x === null && pul > 0) {
				x = -pul;
				krok = krokPasu(stopa);
			}
			if (x !== null) {
				if (!stoji) x += RYCHLOST;
				if (skok !== 0) {
					const d = skok * 0.14;
					x += d;
					skok -= d;
					if (Math.abs(skok) < 1) {
						x += skok;
						skok = 0;
					}
				}
				if (x >= 0) x -= pul;
				if (x < -pul) x += pul;
				stopa.style.transform = 'translateX(' + x + 'px)';
			}
			window.requestAnimationFrame(snimek);
		};
		const rozjedRaf = () => {
			if (rafBezi || jeMobil()) return;
			rafBezi = true;
			window.requestAnimationFrame(snimek);
		};

		const prev = root.querySelector('.rprev');
		const next = root.querySelector('.rnext');
		if (prev) prev.addEventListener('click', () => { skok += krok; });
		if (next) next.addEventListener('click', () => { skok -= krok; });

		if (!klid) {
			vyrez.addEventListener('mouseenter', () => { stoji = true; });
			vyrez.addEventListener('mouseleave', () => { stoji = false; });
		}

		/* ---------------------------------------------------------------
		 * MOBILNÍ SAMOVOLNÝ POSUN (19. 9. 2026, rozhodnutí majitele)
		 * ------------------------------------------------------------- */
		let casovac = null;
		let prevzato = false;   // uživatel se pásu dotkl → TRVALE stop
		let vHledu = false;

		const zastav = () => {
			if (casovac) { window.clearTimeout(casovac); casovac = null; }
		};

		const posun = () => {
			casovac = null;
			if (prevzato || klid || !jeMobil() || !vHledu || document.hidden) return;

			/*
			 * NÁVRAT O PŘESNOU KOPII. Jakmile je pás za polovinou stopy,
			 * odečte se polovina BEZ animace — obsah je od té chvíle
			 * totožný, takže se nic nepohne opticky. Dělá se to PŘED
			 * posunem, ne po něm: po `scrollBy` se `smooth` ještě chvíli
			 * dojíždí a zápis do `scrollLeft` uprostřed by animaci useknul.
			 */
			const pul = vyrez.scrollWidth / 2;
			if (pul > 0 && vyrez.scrollLeft >= pul) vyrez.scrollLeft -= pul;

			const d = krokPasu(stopa);
			if (d > 0) vyrez.scrollBy({ left: d, behavior: 'smooth' });
			naplanuj();
		};

		const naplanuj = () => {
			if (prevzato || klid || !jeMobil() || !vHledu || document.hidden) return;
			zastav();
			casovac = window.setTimeout(posun, PERIODA);
		};

		/*
		 * PŘEVZETÍ KONTROLY. Sledují se vstupní události, ne `scroll` —
		 * `scroll` vyvolá i náš vlastní `scrollBy`, takže by se posun
		 * zastavil hned po prvním kroku sám od sebe. `pointerdown`
		 * a `touchstart` pokrývají prst, `wheel` trackpad v zúženém okně
		 * a `keydown` klávesnici.
		 */
		const prevzetiKontroly = () => {
			if (prevzato) return;
			prevzato = true;
			zastav();
		};
		for (const ev of ['pointerdown', 'touchstart', 'wheel', 'keydown']) {
			vyrez.addEventListener(ev, prevzetiKontroly, { passive: true });
		}

		if (window.IntersectionObserver) {
			const pozorovatel = new window.IntersectionObserver((zaznamy) => {
				for (const z of zaznamy) {
					vHledu = z.isIntersecting;
					if (vHledu) naplanuj();
					else zastav();
				}
			}, { threshold: 0.25 });
			pozorovatel.observe(root);
		} else {
			// Bez pozorovatele je lepší nespouštět nic než posouvat naslepo.
			vHledu = false;
		}

		document.addEventListener('visibilitychange', () => {
			if (document.hidden) zastav();
			else naplanuj();
		});

		/* ---------------------------------------------------------------
		 * PŘEPÍNÁNÍ REŽIMŮ PŘI ZMĚNĚ ŠÍŘKY — musí fungovat OBĚMA SMĚRY
		 * ------------------------------------------------------------- */
		const prepni = () => {
			if (jeMobil()) {
				/*
				 * Inline `transform` po marquee zůstane v atributu, i když
				 * ho CSS na mobilu ruší `!important`. Maže se, aby při
				 * návratu na desktop nezačínal pás uprostřed a aby v DOM
				 * nezůstával stav, který nic nepoužívá.
				 */
				stopa.style.transform = '';
				x = null;
				naplanuj();
			} else {
				zastav();
				rozjedRaf();
			}
		};
		if (mqMobil) mqMobil.addEventListener('change', prepni);
		prepni();
	}
	return pocet;
}
